import type { PublicV2Client, PublicV2Config } from "./public-v2";
import type { PublicV2Purchase } from "./public-v2";

export const publicV2QueryDefaults = Object.freeze({ retry: 0 as const });
export const PUBLIC_V2_PAGE_LIMIT = 12;
export const PUBLIC_V2_MAX_PAGES = 4;
export type PublicV2Cursor = Readonly<{ afterBlock: string; afterLog: string }> | null;
export function publicV2PurchasesQueryOptions(
  config: PublicV2Config,
  client: PublicV2Client | undefined,
  options: { limit?: number; creator?: string; enabled?: boolean } = {},
) {
  const limit = options.limit ?? PUBLIC_V2_PAGE_LIMIT;
  const queryKey = config.kind === "configured"
    ? publicV2QueryKey(config, "purchases", options.creator ?? null, limit)
    : ["registry-v2", "unavailable", "purchases"] as const;
  return {
    queryKey,
    initialPageParam: null as PublicV2Cursor,
    queryFn: ({ pageParam, signal }: { pageParam: PublicV2Cursor; signal: AbortSignal }) => {
      if (config.kind !== "configured") throw new Error("public configuration unavailable");
      if (!client) throw new Error("public client unavailable");
      return client.purchases({ limit, after: pageParam, creator: options.creator, signal });
    },
    getNextPageParam: (last: { nextCursor: PublicV2Cursor }) => last.nextCursor ?? undefined,
    enabled: config.kind === "configured" && client !== undefined && (options.enabled ?? true),
    retry: publicV2QueryDefaults.retry,
    maxPages: PUBLIC_V2_MAX_PAGES,
  };
}
export function publicV2QueryKey(config: Extract<PublicV2Config, { kind: "configured" }>, resource: string, ...parts: readonly unknown[]) {
  const r = config.reference;
  return ["registry-v2", config.apiOrigin, r.protocol, r.chainId, r.registryAddress, r.schemaVersion,
    r.deploymentBlockNumber, r.deploymentBlockHash, r.deploymentTransactionHash, r.registryRuntimeCodeHash, r.sourceCommitment,
    resource, ...parts] as const;
}
export function cursorPageUrl(origin: string, cursor: { afterBlock: string; afterLog: string } | null): string {
  const url = new URL("/v2/purchases", origin);
  if (cursor) { url.searchParams.set("afterBlock", cursor.afterBlock); url.searchParams.set("afterLog", cursor.afterLog); }
  return url.toString();
}
export function formatV2Lifecycle(purchase: PublicV2Purchase): string {
  const labels: Record<PublicV2Purchase["phase"], string> = {
    awaiting_prompt: "Awaiting prompt upload",
    queued: "Queued",
    authorized: "Authorized; readiness not yet attested",
    ready: "Ready by on-chain attestation; hosting is not verified",
    refunded: "Refund credited; withdrawal is separate",
    settled_success: "Settlement credited; cleanup is an attestation",
  };
  return `${purchase.name ?? "Metadata incomplete"} · ${labels[purchase.phase]}`;
}
